import React, { useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import type { ProfileLinkConfig, UserProfile } from "../../../../../client";
import {
  Avatar,
  Checkbox,
  Input,
  isString,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../components";
import { cn } from "../../../../../components/utils/cn";
import { getImageUrl } from "../../../../../utils/image";
import { ProjectSettingsContainer } from "./ProjectSettingsContainer";
import {
  DynamicInput,
  DynamicInputRef,
} from "../../../../../components/input/DynamicInput";
import { useUpdateProfileLinkMotivation } from "../../../../../services/organtization/update-profile-link-motivation";
import { LeadRowCard } from "../card/LeadRowCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfoCircle,
  faBullseye,
  faInfo,
} from "@fortawesome/free-solid-svg-icons";
import { EditorInput } from "../../../../../components/input/EditorInput";
import { faCircleCheck } from "@fortawesome/free-regular-svg-icons";
import { useStatsigClient } from "../../../../../hooks";
import { useThemeColors } from "../../../../../contexts";

interface Props {
  linkConfigData: ProfileLinkConfig;
  disabled: boolean;
  handleUpdateLink: (data: Partial<ProfileLinkConfig>) => void;
  profile?: UserProfile;
  title: string;
  organizationId: string;
  leadId: string;
  linkId: string;
  profilePitch: string;
  assignmentInfoAdded: boolean;
  isDefaultLead: boolean;
}

export const LinkCreatorPersonalBlock = ({
  linkConfigData,
  disabled,
  handleUpdateLink,
  profile,
  title,
  organizationId,
  leadId,
  linkId,
  profilePitch,
  assignmentInfoAdded,
  isDefaultLead,
}: Props) => {
  const [editedPitch, setEditedPitch] = useState(
    linkConfigData?.editedPitch ?? "",
  );
  const [assignmentPitch, setAssignmentPitch] = useState(
    linkConfigData?.assignmentPitch ?? "",
  );
  const [isGeneratingAssigmentPitch, setIsGeneratingAssigmentPitch] =
    useState(false);
  const editedPitchInputRef = useRef<DynamicInputRef>(null);
  const statsigClient = useStatsigClient();

  const { primary } = useThemeColors();

  useEffect(() => {
    setAssignmentPitch(linkConfigData.assignmentPitch || "");
    setEditedPitch(linkConfigData.editedPitch || "");
  }, [linkConfigData]);

  const debouncedEditedPitch = useDebouncedCallback<(val: string) => void>(
    (value) => {
      handleUpdateLink({
        editedPitch: value,
      });
    },
    500,
  );

  const debouncedAssignmentPitch = useDebouncedCallback<(val: string) => void>(
    (value) => {
      handleUpdateLink({
        assignmentPitch: value,
      });
    },
    500,
  );

  const updateProfileAssignmentMotivation = useUpdateProfileLinkMotivation({
    onMutate: () => {
      setIsGeneratingAssigmentPitch(true);
    },
    onSuccess: (config) => {
      setIsGeneratingAssigmentPitch(false);
      if (config.assignmentPitch) {
        handleUpdateLink({ assignmentPitch: config.assignmentPitch });
        setAssignmentPitch(config.assignmentPitch);
      }
    },
    onSettled() {
      setIsGeneratingAssigmentPitch(false);
    },
  });

  return (
    <div className="flex flex-col grow gap-6">
      <div className="flex flex-row items-center gap-4">
        <Checkbox
          checked={
            linkConfigData?.profileLinkPersonalInfoConfig?.showPersonalInfo
          }
          disabled={disabled}
          onCheckedChange={(checked) => {
            if (typeof checked !== "boolean") return;

            handleUpdateLink({
              profileLinkPersonalInfoConfig: {
                ...linkConfigData?.profileLinkPersonalInfoConfig,
                showPersonalInfo: checked,
              },
            });
          }}
        />

        <ProjectSettingsContainer
          className="flex flex-row items-center"
          enabled={
            linkConfigData?.profileLinkPersonalInfoConfig?.showPersonalInfo ??
            false
          }
        >
          <Avatar
            alt={
              typeof profile?.profileData.name === "string"
                ? profile.profileData.name
                : "Profile"
            }
            imageUrl={
              profile?.profilePhotoId
                ? getImageUrl(profile.profilePhotoId)
                : undefined
            }
            borderColor={primary}
            className={cn(
              "w-20 h-20 transition-all duration-300",
              linkConfigData?.profileLinkPersonalInfoConfig?.roundMode
                ? "rounded-[5rem]"
                : "",
              linkConfigData?.profileLinkPersonalInfoConfig?.useBorder &&
                statsigClient?.checkGate("enable_branding_border")
                ? "border-4"
                : "",
              linkConfigData?.useProfilePicture
                ? "scale-100 opacity-100 mr-4"
                : "scale-0 opacity-0 w-0 mr-0",
            )}
          />

          <div className="flex flex-col">
            <p className="text-sm font-semibold">
              {typeof profile?.profileData.name === "string"
                ? profile.profileData.name
                : "Profile"}
            </p>
            {typeof profile?.profileData.title === "string" && (
              <p className="text-sm">{title}</p>
            )}
          </div>
        </ProjectSettingsContainer>
      </div>

      <div className="flex flex-row items-start gap-4">
        <div>
          <Checkbox
            checked={
              linkConfigData?.profileLinkPersonalInfoConfig?.showPitch ?? false
            }
            disabled={disabled}
            onCheckedChange={(checked) => {
              if (typeof checked !== "boolean") return;

              handleUpdateLink({
                profileLinkPersonalInfoConfig: {
                  ...linkConfigData?.profileLinkPersonalInfoConfig,
                  showPitch: checked,
                },
              });
            }}
          />
          {editedPitch && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="h-full justify-center items-center flex mt-5">
                    <FontAwesomeIcon icon={faBullseye} color="#5167FF" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex flex-row items-center justify-center p-4 gap-2">
                    <FontAwesomeIcon icon={faInfo} />
                    <p>
                      Content has been edited and will not sync with the master
                      profile.
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <ProjectSettingsContainer
          className="flex flex-col w-full"
          enabled={
            linkConfigData?.profileLinkPersonalInfoConfig?.showPitch ?? false
          }
        >
          <div className="flex flex-row items-start w-full">
            <div className="flex-grow">
              <DynamicInput
                className="text-xs whitespace-pre-line w-full"
                inputClassName="text-xs"
                disabled={disabled || isDefaultLead}
                label=""
                validate={isString}
                value={editedPitch || (profilePitch != "" ? profilePitch : "")}
                ref={editedPitchInputRef}
                rows={1}
                onBlur={(event) => {
                  const inputValue = event.target.value;
                  setEditedPitch(inputValue);
                  debouncedEditedPitch(inputValue);
                }}
              />
            </div>
          </div>
        </ProjectSettingsContainer>
      </div>
      {!statsigClient?.checkGate("enable_profile_summary") && (
        <div className="flex flex-row items-start gap-4">
          <Checkbox
            checked={linkConfigData?.showAssignmentPitch ?? false}
            disabled={!assignmentInfoAdded}
            onCheckedChange={(checked) => {
              if (typeof checked !== "boolean") return;

              if (linkConfigData.assignmentPitch === "" && checked) {
                updateProfileAssignmentMotivation.mutate({
                  path: {
                    organizationID: organizationId,
                    leadID: leadId,
                    linkID: linkId,
                  },
                  body: {
                    includeName: false,
                  },
                });
              }
              handleUpdateLink({
                showAssignmentPitch: checked,
              });
            }}
          />
          <ProjectSettingsContainer
            className="flex flex-row items-center grow"
            enabled={
              (linkConfigData?.showAssignmentPitch || !assignmentInfoAdded) ??
              false
            }
          >
            {assignmentInfoAdded ? (
              isGeneratingAssigmentPitch ? (
                <p className="text-sm mt-2">Generating profile summary...</p>
              ) : (
                <Input
                  className="w-full bg-gray-1 focus-within:bg-gray-1"
                  inputClassName="text-xs"
                  placeholder="Add a custom motivation on why this profile is a good fit for this assignment"
                  value={assignmentPitch}
                  rows={5}
                  onChange={(e) => {
                    const { value } = e.target;
                    setAssignmentPitch(value);
                    debouncedAssignmentPitch(value);
                  }}
                />
              )
            ) : (
              <LeadRowCard
                className="text-xs bg-red-1 border-red-2"
                type="dashed"
              >
                <div className="flex flex-1 items-center justify-between gap-3">
                  <FontAwesomeIcon
                    width={14}
                    height={14}
                    icon={faInfoCircle}
                    className="w-4 h-4 shrink-0"
                  />
                  <p>
                    If you want to add a custom motivation on why this profile
                    is a good fit, please add assignment information first.
                  </p>
                </div>
              </LeadRowCard>
            )}
          </ProjectSettingsContainer>
        </div>
      )}
    </div>
  );
};
